import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { HrFileStep } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportTable, type ReportColumn } from "@/components/app/documents/report-table";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { Badge } from "@/components/ui/badge";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { StepCell } from "@/routes/_authenticated/_workforce/hr/-components/step-cell";

export const Route = createFileRoute("/_authenticated/_workforce/hr/completion")({
  component: HrCompletion,
});

type Row = Output<typeof orpcClient.workforce.employeeDocuments.employees.overview>[number];

/** One column per step, found by `key` not position, so no step (onboarding) is ever missing or
 * mislabelled. */
const stepOf = (row: Row, key: HrFileStep["key"]): HrFileStep =>
  row.steps.find((step) => step.key === key) ?? {
    key,
    label: key,
    applicable: false,
    satisfied: false,
    evidence: "Not reported",
  };

const stepColumn = (key: HrFileStep["key"], header: string): ReportColumn<Row> => ({
  header,
  cell: (row) => <StepCell step={stepOf(row, key)} />,
  value: (row) => stepOf(row, key).evidence,
});

const COLUMNS: ReadonlyArray<ReportColumn<Row>> = [
  {
    header: "Employee",
    cell: (row) => (
      <Link
        to="/employees/$userId"
        params={{ userId: row.id }}
        className="font-medium hover:underline"
      >
        {row.name}
      </Link>
    ),
    value: (row) => row.name,
  },
  { header: "Job title", cell: (row) => row.jobTitle ?? "-", value: (row) => row.jobTitle },
  stepColumn("documents", "Documents"),
  stepColumn("training", "Training"),
  stepColumn("delegation", "RN delegation"),
  stepColumn("onboarding", "Onboarding"),
  {
    header: "File",
    align: "right",
    // A file with no step that applies has nothing to fail: red "0/0" said
    // it was the worst file on the page.
    cell: (row) =>
      row.progress.total === 0 ? (
        <Badge variant="neutral">No steps apply</Badge>
      ) : (
        <Badge
          variant={row.progress.complete ? "ok" : row.progress.done === 0 ? "crit" : "warn"}
        >{`${row.progress.done}/${row.progress.total} steps`}</Badge>
      ),
    value: (row) => `${row.progress.done}/${row.progress.total}`,
    sortBy: (row) => (row.progress.total === 0 ? -1 : row.progress.done / row.progress.total),
  },
];

/** Where each employee's file stands, one verdict each. Facts and verdicts come from the server, so
 * this cannot disagree with the Employees screen. Incomplete first. */
function HrCompletion() {
  const { facilityId } = useFacility();
  const overview = useQuery(
    orpc.workforce.employeeDocuments.employees.overview.queryOptions({
      input: { facilityId, includeInactive: false },
    }),
  );

  const rows: Row[] = (overview.data ?? []).toSorted(
    (left, right) =>
      Number(left.progress.complete) - Number(right.progress.complete) ||
      left.name.localeCompare(right.name),
  );

  const complete = rows.filter((row) => row.progress.complete).length;
  const nothingDone = rows.filter(
    (row) => row.progress.total > 0 && row.progress.done === 0,
  ).length;

  return (
    <>
      <PageHeader
        title="File completion"
        meta="Where each employee's paperwork, training and onboarding stand, in one line."
      />

      {/* Counted only once read: zeros from a failed read would say nobody's
          file is complete, which is a claim nobody made. */}
      {overview.data ? (
        <StatTiles>
          <StatTile label="Employees" value={rows.length} />
          <StatTile label="Files complete" value={complete} />
          <StatTile
            label="Nothing on file"
            value={nothingDone}
            hint="No step satisfied yet"
            tone={nothingDone > 0 ? "warn" : undefined}
          />
        </StatTiles>
      ) : null}

      {overview.isError ? (
        <ReadFailed
          message="Could not load file completion. Nothing here means nothing was read, not that every file is complete."
          error={overview.error}
          onRetry={() => void overview.refetch()}
          retrying={overview.isFetching}
        />
      ) : (
        <ReportTable
          rows={overview.isPending ? undefined : rows}
          pending={overview.isPending}
          rowKey={(row) => row.id}
          columns={COLUMNS}
          filename="hr-file-completion.csv"
          empty={{
            title: "No employees to track",
            description: "Employees appear here once they have an account.",
          }}
        />
      )}
    </>
  );
}
